// ============================================================================
// pollMethods.ts — КАТАЛОГ МЕТОДИК (типов опросов) для интерфейса психолога
// и страницы «Тесты и анкеты».
//
// SSoT — сервер: типы опросов (PollService.listPollTypes), шаблоны
// (PollService.listPolls) и вопросы (PollService.getPollFull). Здесь только
// чтение существующими RPC и производные признаки:
//   • число вопросов — из шаблона (0 = внешняя авторская методика, результат
//     вносится вручную);
//   • КАТЕГОРИЯ — производная от РЕАЛЬНЫХ полей (scoring + наличие вопросов).
//     Отдельного поля «категория» в API типа опроса НЕТ (зафиксировано в
//     отчёте задачи) — поэтому категория не выдумывается, а выводится
//     детерминированно из scoring/вопросов.
//
// Чтение — через durable-кэш (офлайн-фёрст): каталог переживает перезагрузку
// без сервера. Ошибка чтения вопросов ОДНОЙ методики не рушит каталог —
// методика остаётся доступной, а неточность честно показывается в предупреждении.
// ============================================================================

import type { Poll, PollTypeInfo } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { readThrough } from '@/lib/offlineCache';
import { logSwallowed } from '@/lib/errors';

export type PollMethodCategory = 'screening' | 'scales' | 'questionnaire' | 'external' | 'other';

/** Карточка методики (тип опроса + шаблон). */
export interface PollMethodItem {
  code: string;
  name: string;
  synonym: string;
  description: string;
  scoring: string;
  status: string;
  templateId: string;
  /** Число вопросов шаблона; null — прочитать не удалось. */
  questionCount: number | null;
  category: PollMethodCategory;
  /** Внешняя (авторская) методика: вопросов нет, результат вносится вручную. */
  manualOnly: boolean;
}

export interface PollMethodCatalog {
  items: PollMethodItem[];
  /** Методики, у которых не удалось прочитать шаблон (частичный сбой). */
  warnings: string[];
}

export const METHOD_CATEGORY_ORDER: PollMethodCategory[] = [
  'screening', 'scales', 'questionnaire', 'external', 'other',
];

export const METHOD_CATEGORY_LABEL: Record<PollMethodCategory, string> = {
  screening: 'Скрининги',
  scales: 'Шкалы',
  questionnaire: 'Анкеты',
  external: 'Авторские методики',
  other: 'Прочие',
};

export const METHOD_CATEGORY_HINT: Record<PollMethodCategory, string> = {
  screening: 'Автоматический подсчёт балла по нормативам и порог прохождения',
  scales: 'Автоматический подсчёт по шкалам',
  questionnaire: 'Вопросы без авто-скоринга — балл и заключение вносит специалист',
  external: 'Контент авторской методики не входит в поставку — результат вносится вручную',
  other: 'Шаблон методики прочитать не удалось — откройте методику для проверки',
};

export const METHOD_SCORING_LABEL: Record<string, string> = {
  engine: 'авто-подсчёт по порогу',
  scales: 'авто-подсчёт по шкалам',
  keys: 'проверка по ключам',
  none: 'без авто-скоринга',
};

function slug(): string {
  return useCenterStore.getState().baseSlug || 'local';
}

/**
 * Категория методики — детерминированно из РЕАЛЬНЫХ данных:
 *   • вопросов нет → внешняя авторская методика (ручной результат);
 *   • scoring = engine → скрининг (сумма/правила + порог);
 *   • scoring = scales → шкалы;
 *   • вопросы есть, авто-скоринга нет → анкета;
 *   • шаблон не прочитан → «прочие» (без выдумывания категории).
 */
export function classifyMethod(scoring: string, questionCount: number | null): PollMethodCategory {
  if (questionCount === 0) return 'external';
  const mode = String(scoring || '').trim().toLowerCase();
  if (mode === 'engine') return 'screening';
  if (mode === 'scales') return 'scales';
  if (questionCount === null) return 'other';
  return 'questionnaire';
}

/** Категория и признак «только ручной результат» из числа вопросов. */
export function methodTraits(scoring: string, questionCount: number | null): {
  category: PollMethodCategory;
  manualOnly: boolean;
} {
  return {
    category: classifyMethod(scoring, questionCount),
    manualOnly: questionCount === 0,
  };
}

/** Короткая строка признаков методики для карточки (без выдуманных данных). */
export function methodMeta(item: PollMethodItem): string {
  const parts: string[] = [];
  if (item.questionCount === null) {
    parts.push('вопросы не прочитаны');
  } else if (item.questionCount === 0) {
    parts.push('без вопросов · ручной результат');
  } else {
    parts.push(`${item.questionCount} ${questionWord(item.questionCount)}`);
  }
  const scoring = METHOD_SCORING_LABEL[String(item.scoring || '').toLowerCase()];
  if (scoring) parts.push(scoring);
  return parts.join(' · ');
}

export function questionWord(count: number): string {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return 'вопросов';
  if (n1 === 1) return 'вопрос';
  if (n1 >= 2 && n1 <= 4) return 'вопроса';
  return 'вопросов';
}

function buildItem(type: PollTypeInfo, poll: Poll | undefined, questionCount: number | null): PollMethodItem {
  const scoring = String(type.scoring || poll?.scoring || '');
  const traits = methodTraits(scoring, questionCount);
  return {
    code: String(type.code || ''),
    name: String(type.name || ''),
    synonym: String(type.synonym || type.name || type.code || ''),
    description: String(poll?.description || ''),
    scoring,
    status: String(type.status || poll?.status || ''),
    templateId: String(type.templateId || ''),
    questionCount,
    category: traits.category,
    manualOnly: traits.manualOnly,
  };
}

// Кэш в памяти процесса: повторные монтирования страницы не делают 20+ запросов.
const CATALOG_MEMORY_TTL_MS = 60_000;
let catalogMemo: { key: string; at: number; data: PollMethodCatalog } | null = null;

/**
 * Каталог методик: типы опросов + шаблоны + число вопросов.
 * Все вызовы — существующие RPC; новых серверных механизмов нет.
 * `force` — явное обновление (кнопка «Обновить»), иначе 60 секунд отдаётся
 * загруженный каталог.
 */
export async function loadPollMethodCatalog(options?: { force?: boolean }): Promise<PollMethodCatalog> {
  const key = `poll:catalog:${slug()}`;
  if (!options?.force && catalogMemo && catalogMemo.key === key
    && Date.now() - catalogMemo.at < CATALOG_MEMORY_TTL_MS) {
    return catalogMemo.data;
  }
  const res = await readThrough<PollMethodCatalog>(key, async () => {
    const sdk = getSDK();
    const [typesRes, pollsRes] = await Promise.all([
      sdk.poll.listPollTypes({}),
      sdk.poll.listPolls({}),
    ]);
    const pollById = new Map<string, Poll>();
    for (const p of pollsRes.items) pollById.set(String(p.id), p);
    const types = typesRes.items.filter((t: PollTypeInfo) => String(t.code || '') && String(t.templateId || ''));

    // Число вопросов — по шаблону. Частичный сбой не скрывает методику.
    const counts = await Promise.allSettled(
      types.map((t) => sdk.poll.getPollFull({ id: String(t.templateId) })),
    );
    const warnings: string[] = [];
    const items = types.map((t, index) => {
      const poll = pollById.get(String(t.templateId));
      const settled = counts[index];
      let questionCount: number | null = null;
      if (settled.status === 'fulfilled') {
        questionCount = Array.isArray(settled.value.questions) ? settled.value.questions.length : 0;
      } else {
        logSwallowed(settled.reason, `pollMethods: вопросы «${t.synonym || t.code}»`);
        warnings.push(String(t.synonym || t.name || t.code || '').trim());
      }
      return buildItem(t, poll, questionCount);
    });

    items.sort((a, b) => {
      const ca = METHOD_CATEGORY_ORDER.indexOf(a.category);
      const cb = METHOD_CATEGORY_ORDER.indexOf(b.category);
      if (ca !== cb) return ca - cb;
      return a.synonym.localeCompare(b.synonym, 'ru');
    });
    return { items, warnings };
  });
  catalogMemo = { key, at: Date.now(), data: res.data };
  return res.data;
}

/** Нормализация поисковой строки (регистр, лишние пробелы). */
function normalizeQuery(query: string): string {
  return String(query || '').trim().toLowerCase();
}

/** Поиск по названию/синониму/коду/описанию и категории. */
export function filterPollMethods(
  items: PollMethodItem[], query: string, category: PollMethodCategory | 'all',
): PollMethodItem[] {
  const q = normalizeQuery(query);
  return items.filter((item) => {
    if (category !== 'all' && item.category !== category) return false;
    if (!q) return true;
    const haystack = [
      item.synonym, item.name, item.code, item.description,
      METHOD_CATEGORY_LABEL[item.category],
    ].join(' ').toLowerCase();
    return haystack.includes(q);
  });
}

export interface PollMethodCounts {
  all: number;
  byCategory: Partial<Record<PollMethodCategory, number>>;
}

/** Счётчики категорий — для чипов (показываем только реально существующие). */
export function countPollMethods(items: PollMethodItem[]): PollMethodCounts {
  const byCategory: Partial<Record<PollMethodCategory, number>> = {};
  for (const item of items) {
    byCategory[item.category] = (byCategory[item.category] || 0) + 1;
  }
  return { all: items.length, byCategory };
}
